/**
 * (c) 2024, Micro:bit Educational Foundation and contributors
 *
 * SPDX-License-Identifier: MIT
 */
import { App as CapacitorApp } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { Encoding, Filesystem } from "@capacitor/filesystem";
import { useToast } from "@microbit/ui";
import {
  EditorWorkspaceSaveRequest,
  MakeCodeFrameDriver,
  MakeCodeFrameProps,
  MakeCodeProject,
} from "@microbit/makecode-embed/react";
import {
  createContext,
  ReactNode,
  RefObject,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router";
import { useDeployment } from "../deployment";
import { useDownloadActions } from "../download-flow/download-hooks";
import { useLogging } from "../logging/logging-hooks";
import {
  HexData,
  isDatasetUserFileFormat,
  PostImportDialogState,
  SaveStep,
  SaveType,
} from "../model";
import { isIOS } from "../platform";
import {
  untitledProjectName as untitled,
  untitledProjectName,
} from "../project-utils";
import { useStore } from "../store";
import {
  createCodePageUrl,
  createDataSamplesPageUrl,
  createTestingModelPageUrl,
  TestingModelPageHistoryState,
} from "../urls";
import { getTotalNumSamples } from "../utils/actions";
import {
  downloadHexData,
  getLowercaseFileExtension,
  readFileAsText,
} from "../utils/fs-util";
import { isShareCanceled, shareFile } from "../utils/share-util";
import { isWebUrl } from "../utils/url-util";

class CodeEditorError extends Error {}

/**
 * Distinguishes the different ways to trigger the load action.
 */
export type LoadType = "drop-load" | "file-upload";

/**
 * Distinguishes the whether the file loaded should replace the entire project
 * or just the actions. This depends on where this action was triggered in the UI.
 */
export type LoadAction = "replaceProject" | "replaceActions";

interface ProjectContext {
  browserNavigationToEditor(): Promise<boolean>;
  openEditor(focusVisible?: boolean): Promise<void>;
  project: MakeCodeProject;
  projectEdited: boolean;
  resetProject: () => void;
  loadFile: (
    file: File,
    type: LoadType,
    loadAction: LoadAction
  ) => Promise<void>;
  /**
   * Called to request a save.
   *
   * Pass a project if we already have the content to download. Otherwise it will
   * be requested from the editor.
   *
   * The save is not necessarily complete when this returns as we may be waiting
   * on MakeCode or a dialog flow. The progress will be reflected in the `save`
   * state field.
   */
  saveHex: (saveType: SaveType, hex?: HexData) => Promise<void>;

  editorCallbacks: Pick<
    MakeCodeFrameProps,
    | "onDownload"
    | "onWorkspaceSave"
    | "onWorkspaceLoaded"
    | "onSave"
    | "onBack"
    | "initialProjects"
  >;
}

const ProjectContext = createContext<ProjectContext | undefined>(undefined);

export const useProject = (): ProjectContext => {
  const project = useContext(ProjectContext);
  if (!project) {
    throw new Error("Missing provider");
  }
  return project;
};

interface ProjectProviderProps {
  driverRef: RefObject<MakeCodeFrameDriver>;
  children: ReactNode;
}

export const useDefaultProjectName = (): string => {
  const intl = useIntl();
  return intl.formatMessage({ id: "default-project-name" });
};

export const useProjectIsUntitled = (): boolean => {
  const translatedUntitled = useDefaultProjectName();
  const projectName = useStore((s) => s.project.header?.name);
  return projectName === untitled || projectName === translatedUntitled;
};

export const useProjectName = (): string => {
  const isUntitled = useProjectIsUntitled();
  const translatedUntitled = useDefaultProjectName();
  const projectName = useStore((s) =>
    !s.project.header || isUntitled ? translatedUntitled : s.project.header.name
  );
  return projectName;
};

export const ProjectProvider = ({
  driverRef,
  children,
}: ProjectProviderProps) => {
  const intl = useIntl();
  const { appNameFull } = useDeployment();
  const toast = useToast();
  const logging = useLogging();
  const projectEdited = useStore((s) => s.projectEdited);
  const editorStartUp = useStore((s) => s.editorStartUp);
  const getEditorStartUp = useStore((s) => s.getEditorStartUp);
  const editorReady = useStore((s) => s.editorReady);
  const editorTimedOut = useStore((s) => s.editorTimedOut);
  const setIsEditorTimedOutDialogOpen = useStore(
    (s) => s.setIsEditorTimedOutDialogOpen
  );
  const setProjectEdited = useStore((s) => s.setProjectEdited);
  const setEditorLoadingFile = useStore((s) => s.setEditorLoadingFile);
  const setEditorImportingState = useStore((s) => s.setEditorImportingState);
  const projectFlushedToEditor = useStore((s) => s.projectFlushedToEditor);
  const checkIfProjectNeedsFlush = useStore((s) => s.checkIfProjectNeedsFlush);
  const getCurrentProject = useStore((s) => s.getCurrentProject);
  const setPostImportDialogState = useStore((s) => s.setPostImportDialogState);
  const { editorReadyPromise, editorContentLoadedPromise } = useStore(
    (s) => s.editorPromises
  );
  const startUpTimestamp = useStore((s) => s.editorStartUpTimestamp);
  const langChangeFlushedToEditor = useStore(
    (s) => s.langChangeFlushedToEditor
  );
  const checkIfLangChanged = useStore((s) => s.checkIfLangChanged);
  const navigate = useNavigate();

  const openEditorTimedOutDialog = useCallback(() => {
    setIsEditorTimedOutDialogOpen(true);
  }, [setIsEditorTimedOutDialogOpen]);

  const project = useStore((s) => s.project);
  const initialProjects = useCallback(() => {
    logging.log(
      `[MakeCode] Initialising with header ID: ${project.header?.id}`
    );
    // This is a useful point to introduce a delay to debug MakeCode init dependencies.
    return Promise.resolve([project]);
  }, [logging, project]);

  const startUpTimeout = 90000;

  const onWorkspaceLoaded = useCallback(async () => {
    logging.log("[MakeCode] Workspace loaded");
    await editorContentLoadedPromise.promise;
    // Get latest start up state and only mark editor ready if editor has not timed out.
    if (getEditorStartUp() !== "timed out") {
      editorReady();
    }
    editorReadyPromise.resolve();
  }, [
    editorContentLoadedPromise,
    editorReady,
    editorReadyPromise,
    getEditorStartUp,
    logging,
  ]);

  const onEditorContentLoaded = useCallback(() => {
    logging.log("[MakeCode] Editor content loaded");
    editorContentLoadedPromise.resolve();
  }, [editorContentLoadedPromise, logging]);

  const checkIfEditorStartUpTimedOut = useCallback(
    async (promise: Promise<void> | undefined) => {
      const elapsedTimeSinceStartup = Date.now() - startUpTimestamp;
      const remainingTimeout = startUpTimeout - elapsedTimeSinceStartup;
      if (
        // Editor has already timed out.
        (editorStartUp === "in-progress" && remainingTimeout <= 0) ||
        editorStartUp === "timed out"
      ) {
        return true;
      }
      return await Promise.race([
        promise,
        ...(remainingTimeout > 0
          ? [
              new Promise<true>((resolve) =>
                setTimeout(() => {
                  resolve(true);
                }, remainingTimeout)
              ),
            ]
          : []),
      ]);
    },
    [editorStartUp, startUpTimestamp]
  );

  const doAfterEditorUpdatePromise = useRef<Promise<void>>();
  const doAfterEditorUpdate = useCallback(
    async (action: () => Promise<void>) => {
      if (
        !doAfterEditorUpdatePromise.current &&
        (checkIfProjectNeedsFlush() || checkIfLangChanged())
      ) {
        doAfterEditorUpdatePromise.current = (async () => {
          // driverRef.current is not defined on first render.
          // Only an issue when navigating to code page directly.
          if (!driverRef.current) {
            throw new CodeEditorError("MakeCode iframe ref is undefined");
          } else if (checkIfProjectNeedsFlush()) {
            logging.log("[MakeCode] Importing project");
            await editorReadyPromise.promise;
            const project = getCurrentProject();
            try {
              setEditorImportingState();
              await driverRef.current.importProject({ project });
              logging.log("[MakeCode] Project import succeeded");
              projectFlushedToEditor();
              langChangeFlushedToEditor();
            } catch (e) {
              logging.log("[MakeCode] Project import failed");
              throw e;
            }
          } else {
            logging.log("[MakeCode] Waiting for editor after language change");
            await editorReadyPromise.promise;
            langChangeFlushedToEditor();
          }
        })();
      }

      try {
        const hasTimedOut = await checkIfEditorStartUpTimedOut(
          doAfterEditorUpdatePromise.current
        );
        if (hasTimedOut) {
          editorTimedOut();
          logging.log("[MakeCode] Load timed out");
          logging.event({
            type: "editor_open_failure",
          });
          throw new CodeEditorError("MakeCode load timed out");
        }
      } finally {
        doAfterEditorUpdatePromise.current = undefined;
      }
      return action();
    },
    [
      checkIfProjectNeedsFlush,
      checkIfLangChanged,
      driverRef,
      logging,
      editorReadyPromise.promise,
      getCurrentProject,
      setEditorImportingState,
      projectFlushedToEditor,
      langChangeFlushedToEditor,
      checkIfEditorStartUpTimedOut,
      editorTimedOut,
    ]
  );
  // We can't tell whether back from MakeCode is triggered by mouse or
  // keyboard when re-focusing "Edit in MakeCode" as it's in the iframe.
  // So instead we track whether we opened it by mouse or keyboard.
  const openedViaKeyboardRef = useRef(false);
  const openEditor = useCallback(
    async (focusVisible?: boolean) => {
      openedViaKeyboardRef.current = focusVisible ?? false;
      logging.event({
        type: "editor_open",
      });
      try {
        setProjectEdited();
        await doAfterEditorUpdate(() => {
          void navigate(createCodePageUrl());
          return Promise.resolve();
        });
      } catch (e) {
        if (e instanceof CodeEditorError) {
          openEditorTimedOutDialog();
        }
      }
    },
    [
      doAfterEditorUpdate,
      logging,
      navigate,
      openEditorTimedOutDialog,
      setProjectEdited,
    ]
  );
  const browserNavigationToEditor = useCallback(async () => {
    try {
      setProjectEdited();
      await doAfterEditorUpdate(() => {
        return Promise.resolve();
      });
      return true;
    } catch (e) {
      if (e instanceof CodeEditorError) {
        // In this case, doAfterEditorUpdate has failed because the app has loaded
        // on the code page directly. The caller of browserNavigationToEditor redirects.
        return false;
      }
      // Unexpected error, can't handle better than the redirect.
      logging.error("Error", e);
      return false;
    }
  }, [doAfterEditorUpdate, logging, setProjectEdited]);
  const resetProject = useStore((s) => s.resetProject);
  const loadDataset = useStore((s) => s.loadDataset);
  const setLoadingOverlayVisible = useStore((s) => s.setLoadingOverlayVisible);
  const importProjectFromHexText = useCallback(
    async (hex: string, fileName: string) => {
      const makeCodeMagicMark = "41140E2FB82FA2BB";
      // Check if is a MakeCode hex, otherwise show error dialog.
      if (!hex.includes(makeCodeMagicMark)) {
        setPostImportDialogState(PostImportDialogState.Error);
        setLoadingOverlayVisible(false);
        return;
      }
      const hasTimedOut = await checkIfEditorStartUpTimedOut(
        editorReadyPromise.promise
      );
      if (hasTimedOut) {
        openEditorTimedOutDialog();
        setLoadingOverlayVisible(false);
        return;
      }
      // This triggers the code in editorChanged to update actions etc.
      // It is fire-and-forget: loading overlay is cleared by editorChange.
      setEditorLoadingFile();
      driverRef.current!.importFile({
        filename: fileName,
        parts: [hex],
      });
    },
    [
      checkIfEditorStartUpTimedOut,
      driverRef,
      editorReadyPromise.promise,
      openEditorTimedOutDialog,
      setEditorLoadingFile,
      setLoadingOverlayVisible,
      setPostImportDialogState,
    ]
  );
  const loadFile = useCallback(
    async (
      file: File,
      type: LoadType,
      loadAction: LoadAction
    ): Promise<void> => {
      try {
        const fileExtension = getLowercaseFileExtension(file.name);
        const source = type === "drop-load" ? "drop" : "file_picker";
        if (fileExtension === "json") {
          setLoadingOverlayVisible(true);
          logging.event({ type: "dataset_load", detail: { source } });
          const actionsString = await readFileAsText(file);
          const actions = JSON.parse(actionsString) as unknown;
          if (isDatasetUserFileFormat(actions)) {
            await loadDataset(actions, loadAction);
            void navigate(createDataSamplesPageUrl());
          } else {
            setPostImportDialogState(PostImportDialogState.Error);
          }
          setLoadingOverlayVisible(false);
        } else if (fileExtension === "hex") {
          setLoadingOverlayVisible(true);
          logging.event({ type: "project_import", detail: { source } });
          const hex = await readFileAsText(file);
          await importProjectFromHexText(hex, file.name);
          // importProjectFromHexText clears loading overlay.
        } else {
          setPostImportDialogState(PostImportDialogState.Error);
        }
      } catch (e) {
        setLoadingOverlayVisible(false);
        throw e;
      }
    },
    [
      importProjectFromHexText,
      loadDataset,
      logging,
      navigate,
      setLoadingOverlayVisible,
      setPostImportDialogState,
    ]
  );

  const setSave = useStore((s) => s.setSave);
  const save = useStore((s) => s.save);
  const settings = useStore((s) => s.settings);
  const actions = useStore((s) => s.actions);
  const saveNextDownloadRef = useRef<SaveType | null>(null);
  const translatedUntitled = useDefaultProjectName();
  const saveHex = useCallback(
    async (saveType: SaveType, hex?: HexData): Promise<void> => {
      const { step } = save;
      const projectName = getCurrentProject().header?.name;
      if (settings.showPreSaveHelp && step === SaveStep.None) {
        setSave({ hex, step: SaveStep.PreSaveHelp, type: saveType });
      } else if (
        (projectName === untitled || projectName === translatedUntitled) &&
        step === SaveStep.None
      ) {
        setSave({ hex, step: SaveStep.ProjectName, type: saveType });
      } else if (!hex) {
        setSave({ hex, step: SaveStep.SaveProgress, type: saveType });
        // This will result in a future call to saveHex with a hex.
        try {
          await doAfterEditorUpdate(async () => {
            saveNextDownloadRef.current = saveType;
            await driverRef.current!.compile();
          });
        } catch (e) {
          if (e instanceof CodeEditorError) {
            setSave({ step: SaveStep.None, type: SaveType.Download });
            openEditorTimedOutDialog();
          }
        }
      } else {
        logging.event({
          type: "project_save",
          detail: {
            actions: actions.length,
            samples: getTotalNumSamples(actions),
            destination: saveType,
          },
        });
        // Dismiss the progress dialog now that the hex is ready.
        setSave({ hex, step: SaveStep.ChooseDestination, type: saveType });
        if (saveType === SaveType.Share) {
          try {
            await shareFile(
              `${hex.name}.hex`,
              hex.hex,
              intl.formatMessage(
                { id: "share-named-file" },
                { name: hex.name }
              ),
              intl.formatMessage(
                { id: "share-file-description" },
                { appNameFull, name: hex.name }
              )
            );
          } catch (e) {
            if (!isShareCanceled(e)) {
              logging.error("Sharing failed", e);
            }
          }
        } else {
          await downloadHexData(hex);
        }
        setSave({ step: SaveStep.None, type: SaveType.Download });
        if (saveType === SaveType.Download && !isIOS()) {
          // iOS share sheet provides its own feedback.
          toast({
            id: "save-complete",
            title: intl.formatMessage({ id: "saving-toast-title" }),
            status: "info",
          });
        }
      }
    },
    [
      appNameFull,
      save,
      getCurrentProject,
      settings.showPreSaveHelp,
      translatedUntitled,
      setSave,
      doAfterEditorUpdate,
      driverRef,
      openEditorTimedOutDialog,
      logging,
      actions,
      toast,
      intl,
    ]
  );

  // These are event handlers for MakeCode

  const editorChange = useStore((s) => s.editorChange);
  const onWorkspaceSave = useCallback(
    async (event: EditorWorkspaceSaveRequest) => {
      if (!checkIfLangChanged()) {
        // We don't want to handle these events until MakeCode has been
        // reinitialised after a language change.
        // We should reinitialise with the latest project.
        await editorChange(event.project);
      }
    },
    [checkIfLangChanged, editorChange]
  );

  const onBack = useCallback(() => {
    const state: TestingModelPageHistoryState = {
      fromEditor: true,
      focusVisible: openedViaKeyboardRef.current,
    };
    void navigate(createTestingModelPageUrl(), { state });
  }, [navigate]);
  const onSave = useCallback(
    (hex: HexData) => saveHex(SaveType.Download, hex),
    [saveHex]
  );
  const downloadActions = useDownloadActions();
  const onDownload = useCallback(
    (download: HexData) => {
      if (saveNextDownloadRef.current) {
        const saveType = saveNextDownloadRef.current;
        saveNextDownloadRef.current = null;
        void saveHex(saveType, download);
      } else {
        void downloadActions.start(download);
      }
    },
    [downloadActions, saveHex]
  );

  // Native app URL open handler (e.g. opening a .hex file).
  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      const appUrlListener = CapacitorApp.addListener(
        "appUrlOpen",
        async (evt) => {
          // Web deep links (Universal/App Links, e.g. https://.../import?...)
          // are handled by useDeepLinks; this handler only opens files.
          if (isWebUrl(evt.url)) {
            return;
          }
          try {
            setLoadingOverlayVisible(true);
            const contents = await Filesystem.readFile({
              path: evt.url,
              encoding: Encoding.UTF8,
            });
            let filename = decodeURIComponent(
              evt.url.substring(evt.url.lastIndexOf("/") + 1)
            );
            // Forgivingly shim broken filenames to hex files,
            // we can't rely on android to maintain file data.
            // Even Android's Files app often passes us a broken
            // filename. MakeCode is resilient to bad files.
            if (filename.length === 0) {
              filename = `${untitledProjectName}.hex`;
            }
            if (!filename.includes(".")) {
              filename += ".hex";
            }
            await importProjectFromHexText(contents.data as string, filename);
            // importProjectFromHexText clears loading overlay.
          } catch (e) {
            setLoadingOverlayVisible(false);
            throw e;
          }
        }
      );

      return () => {
        void appUrlListener.then((l) => l.remove());
      };
    }
  }, [importProjectFromHexText, setLoadingOverlayVisible]);

  const value = useMemo(
    () => ({
      loadFile,
      openEditor,
      browserNavigationToEditor,
      project,
      projectEdited,
      resetProject,
      saveHex,
      editorCallbacks: {
        initialProjects,
        onSave,
        onWorkspaceSave,
        onDownload,
        onBack,
        onEditorContentLoaded,
        onWorkspaceLoaded,
      },
    }),
    [
      loadFile,
      openEditor,
      browserNavigationToEditor,
      project,
      projectEdited,
      resetProject,
      saveHex,
      initialProjects,
      onSave,
      onWorkspaceSave,
      onDownload,
      onBack,
      onEditorContentLoaded,
      onWorkspaceLoaded,
    ]
  );

  return (
    <ProjectContext.Provider value={value}>{children}</ProjectContext.Provider>
  );
};
